const uuid = require('uuid');

const base64 = require('virtru-base64');
const fs = require('fs');
const internal = require('../tdf');
const EAS = require('./auth/eas');
const UserSettings = require('./auth/user-settings');
const { TDFCiphertextStream, PlaintextStream } = require('./tdf-stream');
const { streamToBuffer } = require('../utils');
const Errors = require('./errors');
const AuthProvider = require('./auth/provider');
const HttpRequest = require('./auth/http-request');
const TdfNavigator = require('../utils/tdf-navigator');

const GLOBAL_BYTE_LIMIT = 64 * 1000 * 1000 * 1000; // 64 GB, see WS-9363.
const HTML_BYTE_LIMIT = 100 * 1000 * 1000; // 100 MB, see WS-9476.

const defaultClientConfig = {
  // No default config for now. Delegate to Virtru wrapper for endpoints.
};

/**
 * An abstraction for protecting and accessing data using TDF3 services.
 */
class Client {
  /**
   * Configure a TDF3 client to connect to the requested EAS.
   * @param {Object} config - configuration parameters
   * @param {AuthProvider|function} [config.authProvider] - auth request interceptor
   * @param {string} [config.easEndpoint] - root path for Entity Attribute Service (deprecated)
   * @param {string} [config.entityObjectEndpoint] - Entity Object path on an Entity Attribute Service
   * @param {string} [config.kasEndpoint] - root path for Key Access Service
   * @param {string} [config.keyRewrapEndpoint] - full path on the KAS to the rewrap endpoint
   * @param {string} [config.keyUpsertEndpoint] - full path on the KAS to the key+policy upsert endpoint
   * @param {object} [config.keypair] - explicit keypair to use to communicate with KAS
   * @param {object} [config.readerUrl] - associated TDF reader service
   * @param {object} [config.userId] - a user id to send to EAS, useful in debug mode
   */
  constructor(config = {}) {
    const clientConfig = { ...defaultClientConfig, ...config };
    if (!clientConfig.authProvider) {
      clientConfig.authProvider = new (require('./auth/no-auth'))();
    }
    const { authProvider, easEndpoint } = clientConfig;
    if (easEndpoint) {
      // TODO(PLAT-626) Move to virtru-sdk
      this.userSettings = new UserSettings(authProvider, easEndpoint);
    }
    if (!clientConfig.kasEndpoint) {
      if (!clientConfig.keyRewrapEndpoint) {
        console.error('KAS definition not found');
      } else {
        clientConfig.kasEndpoint = clientConfig.keyRewrapEndpoint.replace(/\/rewrap$/, '');
      }
    } else {
      if (!clientConfig.keyRewrapEndpoint) {
        clientConfig.keyRewrapEndpoint = `${clientConfig.kasEndpoint}/rewrap`;
      }
      if (!clientConfig.keyUpsertEndpoint) {
        clientConfig.keyUpsertEndpoint = `${clientConfig.kasEndpoint}/upsert`;
      }
    }

    this.eas = new EAS({
      authProvider,
      // NOTE(PLAT-619) Update default entity object path to /v1/entity_object
      endpoint: clientConfig.entityObjectEndpoint || `${easEndpoint}/api/entityobject`,
    });
    this.clientConfig = clientConfig;
  }

  /**
   * Encrypt plaintext into TDF ciphertext. One of the core operations of the Virtru SDK.
   *
   * @param {object} params - Required. All parameters for the encrypt operation, generated using {@link EncryptParamsBuilder#build|EncryptParamsBuilder's build()}.
   * @return {TDFCiphertextStream} - a {@link https://nodejs.org/api/stream.html#stream_class_stream_readable|Readable} stream containing the TDF ciphertext.
   * @see EncryptParamsBuilder
   */
  async encrypt(params) {
    const keypair = await this._getOrCreateKeypair(params.opts);
    const entityObject = await this._fetchEntityObject(keypair);
    const userIsCKS = await this._isUserCKS(entityObject);

    if (userIsCKS) {
      throw new Error('Encrypting as a CKS-enabled user is currently not supported.');
    }

    const policyObject = await this._createPolicyObject(params.scope);
    // TODO: Refactor underlying builder to remove some of this unnecessary config.
    const tdf = internal
      .create()
      .setPrivateKey(keypair.privateKey)
      .setPublicKey(keypair.publicKey)
      .setEncryption({
        type: 'split',
        cipher: 'aes-256-gcm',
      })
      .setDefaultSegmentSize(params.windowSize)
      // set root sig and segment types
      .setIntegrityAlgorithm('hs256', 'gmac')
      .addContentStream(params.source, params.mimeType)
      .setPolicy(policyObject)
      .setEntity(entityObject)
      .setAuthProvider(this.clientConfig.authProvider);

    if (this.clientConfig.kasPublicKey && this.clientConfig.kasEndpoint) {
      tdf.addKeyAccess({
        type: params.offline ? 'wrapped' : 'remote',
        metadata: params.metadata,
        publicKey: this.clientConfig.kasPublicKey,
        url: this.clientConfig.kasEndpoint,
      });
    } else {
      // This will use the EO's default attribute's KAS information.
      tdf.addKeyAccess({
        type: params.offline ? 'wrapped' : 'remote',
        metadata: params.metadata,
      });
    }

    const stream = new TDFCiphertextStream(params.windowSize);
    const byteLimit = params.asHtml ? HTML_BYTE_LIMIT : GLOBAL_BYTE_LIMIT;
    // Await in order to catch any errors from this call.
    // TODO: Write error event to stream and don't await.
    await tdf.writeStream(stream, byteLimit);
    if (!params.asHtml) {
      return stream;
    }

    // Wrap if it's html.
    // FIXME: Support streaming for html format.
    const htmlBuf = internal.wrapHtml(
      await stream.toBuffer(),
      JSON.stringify(tdf.manifest),
      this.clientConfig.readerUrl
    );
    const htmlStream = new TDFCiphertextStream(params.windowSize);
    htmlStream.push(htmlBuf);
    htmlStream.push(null);
    return htmlStream;
  }

  /**
   * Decrypt TDF ciphertext into plaintext. One of the core operations of the Virtru SDK.
   *
   * @param {object} - Required. All parameters for the decrypt operation, generated using {@link DecryptParamsBuilder#build|DecryptParamsBuilder's build()}.
   * @return {PlaintextStream} - a {@link https://nodejs.org/api/stream.html#stream_class_stream_readable|Readable} stream containing the decrypted plaintext.
   * @see DecryptParamsBuilder
   */
  async decrypt(params) {
    const keypair = await this._getOrCreateKeypair(params.opts);
    const entityObject = await this._fetchEntityObject(keypair);
    const tdf = internal
      .create()
      .setPrivateKey(keypair.privateKey)
      .setPublicKey(keypair.publicKey)
      .setEntity(entityObject)
      .setAuthProvider(this.clientConfig.authProvider);
    const source = await this._anySourceToBuffer(params);
    const stream = new PlaintextStream();
    // Await in order to catch any errors from this call.
    // TODO: Write error event to stream and don't await.
    await tdf.readStream(source, stream);
    return stream;
  }

  /**
   * Get the unique policyId associated with TDF ciphertext. Useful for managing authorization policies of encrypted data.
   * <br/><br/>
   * The policyId is embedded in the ciphertext so this is a local operation.
   *
   * @param {object} - Required. Parameters specifying the ciphertext for which to get the policyId,
   * generated using {@link DecryptParamsBuilder#build|DecryptParamsBuilder's build()}.
   * @return {string} - the unique policyId, which can be used for tracking purposes or policy management operations.
   * @see DecryptParamsBuilder
   */
  async getPolicyId(params) {
    const source = await this._anySourceToBuffer(params);
    const zipHelper = new TdfNavigator(source);
    const centralDirectory = await zipHelper.getCentralDirectory();
    const manifest = await zipHelper.getManifest(centralDirectory, '0.manifest.json');
    const policyJson = base64.decode(manifest.encryptionInformation.policy);
    return JSON.parse(policyJson).uuid;
  }

  async _anySourceToBuffer({ source }) {
    // dump stream to buffer
    // we don't support streams anyways (see tdf-navigator.js)
    if (source.type === 'stream') {
      const buf = await streamToBuffer(source.location);
      source = { type: 'buffer', location: buf };
    }

    // Unwrap if it's html.
    // Pull first two bytes from source.
    if (!(await this._isZipFormat(source))) {
      // If NOT zip (html), convert/dump to buffer, unwrap, and continue.
      const htmlBuf = await this._writeSourceToBuffer(source);
      const zipBuf = internal.unwrapHtml(htmlBuf);
      source = { type: 'buffer', location: zipBuf };
    }

    return source;
  }

  /*
   * Create a policy object for an encrypt operation.
   */
  async _createPolicyObject(scope) {
    if (scope.policyObject) {
      // use the client override if provided
      return scope.policyObject;
    }
    const policyId = scope.policyId || uuid.v4();
    return {
      uuid: policyId,
      body: {
        dataAttributes: scope.attributes,
        dissem: scope.dissem,
      },
    };
  }

  async _isZipFormat(source) {
    const magic = await this._getFirstTwoBytesFromSource(source);
    // Convert to string for comparison.
    return magic === 'PK';
  }

  async _getFirstTwoBytesFromSource(source) {
    switch (source.type) {
      case 'buffer':
      case 'file-node':
        return (await new TdfNavigator(source).getChunk(0, 2)).toString();
      case 'file-browser':
        return String.fromCharCode.apply(
          null,
          new Uint8Array(await new TdfNavigator(source).getChunk(0, 2))
        );
      default:
        throw new Error(`Unable to determine source type for decrypt: ${source.type}`);
    }
  }

  async _writeSourceToBuffer(source) {
    switch (source.type) {
      case 'buffer':
        return source.location; // NOOP
      case 'file-browser':
        return new Response(source.location).arrayBuffer();
      case 'file-node':
        return fs.readFileSync(source.location);
      default:
        throw new Error(`Unable to determine source type for decrypt: ${source.type}`);
    }
  }

  /*
   * Fetch an entity object for an encrypt or decrypt operation.
   */
  async _fetchEntityObject({ publicKey }) {
    const { userId } = this.clientConfig;
    return this.eas.fetchEntityObject({
      publicKey,
      ...(userId && { userId }),
    });
  }

  /*
   * Determine if the user is a CKS user
   */
  async _isUserCKS(eo) {
    return this.userSettings && this.userSettings.userIsCKS(eo.userId);
  }

  /*
   * Extract a keypair provided as part of the options dict.
   * Default to using the clientwide keypair, generating one if necessary.
   */
  async _getOrCreateKeypair(opts) {
    if (opts.keypair && opts.keypair.publicKey && opts.keypair.privateKey) {
      return opts.keypair;
    }
    if (this.clientConfig.keypair) {
      return this.clientConfig.keypair;
    }
    this.clientConfig.keypair = await internal.generateKeyPair();
    return this.clientConfig.keypair;
  }
}

const { EncryptParamsBuilder } = require('./builders');
const { DecryptParamsBuilder } = require('./builders');

module.exports = {
  Client,
  EncryptParamsBuilder,
  DecryptParamsBuilder,
  Errors,
  AuthProvider,
  HttpRequest,
};
